import { useState } from 'react'
import type {
  Requirement,
  RequirementType,
  RequirementPriorityLevel,
  RequirementStatus,
} from '@ispot/shared'
import {
  REQUIREMENT_TYPE_LABELS,
  REQUIREMENT_PRIORITY_LABELS,
  REQUIREMENT_STATUS_LABELS,
  getAllowedRequirementTypesForParent,
} from './constants'

interface AddRequirementModalProps {
  parentId?: string
  requirements: Requirement[]
  onSave: (data: {
    name: string
    description?: string
    type: RequirementType
    parentId?: string
    priority?: RequirementPriorityLevel
    status?: RequirementStatus
  }) => Promise<void>
  onClose: () => void
}

export function AddRequirementModal({ parentId, requirements, onSave, onClose }: AddRequirementModalProps) {
  const initialParent = parentId ? requirements.find(r => r.id === parentId) : undefined
  const defaultType = getAllowedRequirementTypesForParent(initialParent)[0] ?? 'epic'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<RequirementType>(defaultType)
  const [selectedParentId, setSelectedParentId] = useState(parentId ?? '')
  const [priority, setPriority] = useState<RequirementPriorityLevel | ''>('')
  const [status, setStatus] = useState<RequirementStatus>('backlog')
  const [isSaving, setIsSaving] = useState(false)

  const selectedParent = selectedParentId
    ? requirements.find(r => r.id === selectedParentId)
    : undefined
  const allowedTypes = getAllowedRequirementTypesForParent(selectedParent)
  const canChooseType = allowedTypes.length > 1
  const parentOptions = requirements.filter(r => getAllowedRequirementTypesForParent(r).length > 0)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setIsSaving(true)
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        parentId: selectedParentId || undefined,
        priority: priority || undefined,
        status,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const fieldLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    outline: 'none',
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(29,26,21,0.3)' }}
      onClick={onClose}
    >
      <form
        className="rounded-lg w-[480px] max-h-[90vh] overflow-y-auto"
        style={{
          background: 'var(--color-surface)',
          boxShadow: '0 20px 60px rgba(29,26,21,0.15)',
        }}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        {/* Header */}
        <div className="px-6 pt-5">
          <h2 className="font-display text-[18px] font-semibold" style={{ color: 'var(--color-text)' }}>
            Add Requirement
          </h2>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {selectedParent ? `Adding under "${selectedParent.name}"` : 'Create a new requirement in the PRD hierarchy'}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-4">
          <div>
            <label style={fieldLabelStyle}>Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Password Reset Flow"
              style={inputStyle}
              autoFocus
            />
          </div>

          <div>
            <label style={fieldLabelStyle}>Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this requirement..."
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label style={fieldLabelStyle}>Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value as RequirementType)}
                style={inputStyle}
                disabled={!canChooseType}
              >
                {allowedTypes.map((value) => (
                  <option key={value} value={value}>{REQUIREMENT_TYPE_LABELS[value]}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label style={fieldLabelStyle}>Parent</label>
              <select
                value={selectedParentId}
                onChange={e => {
                  const nextParentId = e.target.value
                  const nextParent = nextParentId
                    ? requirements.find(r => r.id === nextParentId)
                    : undefined
                  const nextAllowedTypes = getAllowedRequirementTypesForParent(nextParent)
                  setSelectedParentId(nextParentId)
                  setType(current => nextAllowedTypes.includes(current) ? current : (nextAllowedTypes[0] ?? 'epic'))
                }}
                style={inputStyle}
              >
                <option value="">None (root level)</option>
                {parentOptions.map(r => (
                  <option key={r.id} value={r.id}>
                    {'  '.repeat(r.depth)}{r.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label style={fieldLabelStyle}>Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value as RequirementPriorityLevel | '')}
                style={inputStyle}
              >
                <option value="">None</option>
                {Object.entries(REQUIREMENT_PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label style={fieldLabelStyle}>Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value as RequirementStatus)}
                style={inputStyle}
              >
                {Object.entries(REQUIREMENT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex justify-end gap-2">
          <button
            type="button"
            className="px-4 py-2 text-[13px] font-medium rounded cursor-pointer"
            style={{
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-sans)',
            }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || isSaving}
            className="px-4 py-2 text-[13px] font-medium rounded cursor-pointer disabled:opacity-50"
            style={{
              background: 'var(--color-solution)',
              border: '1px solid var(--color-solution)',
              color: 'var(--color-warm-900)',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {isSaving ? 'Adding...' : 'Add Requirement'}
          </button>
        </div>
      </form>
    </div>
  )
}
